import { useMemo, useRef } from "react";

const CAPTION_CHUNK_TARGET_CHARS = 88;
const CAPTION_CHUNK_MAX_CHARS = 116;
const MIN_STREAMING_TRAILING_CHUNK_CHARS = 32;
const MIN_STREAMING_TRAILING_CHUNK_WORDS = 4;
const ROLLING_CAPTION_LINE_TARGET_CHARS = 38;
const ROLLING_CAPTION_MAX_LINES = 3;
const NON_SPACED_CAPTION_CHAR_PATTERN =
  /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uac00-\ud7af]/u;

interface RollingCaptionTextOptions {
  text: string | null | undefined;
  segmentId?: string | null;
  isFinal?: boolean;
}

interface RollingCaptionLinesOptions extends RollingCaptionTextOptions {
  maxCharsPerLine?: number;
  maxLines?: number;
  appendOnly?: boolean;
}

interface CaptionToken {
  text: string;
  leadingSpace: boolean;
}

function normalizeCaptionText(text: string) {
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`>#]/g, "")
    .replace(/^\s*[-+]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isNonSpacedCaptionChar(char: string) {
  return NON_SPACED_CAPTION_CHAR_PATTERN.test(char);
}

function tokenizeCaptionWords(text: string) {
  const normalized = normalizeCaptionText(text);
  if (!normalized) {
    return [];
  }

  const tokens: CaptionToken[] = [];
  const chunks = normalized.split(/\s+/).filter(Boolean);

  for (const [chunkIndex, chunk] of chunks.entries()) {
    const parts: string[] = [];
    let bufferedPart = "";

    for (const char of Array.from(chunk)) {
      if (isNonSpacedCaptionChar(char)) {
        if (bufferedPart) {
          parts.push(bufferedPart);
          bufferedPart = "";
        }
        parts.push(char);
      } else {
        bufferedPart += char;
      }
    }

    if (bufferedPart) {
      parts.push(bufferedPart);
    }

    for (const [partIndex, part] of parts.entries()) {
      tokens.push({
        text: part,
        leadingSpace: chunkIndex > 0 && partIndex === 0,
      });
    }
  }

  return tokens;
}

function appendCaptionWords(
  previousWords: CaptionToken[],
  nextWords: CaptionToken[],
  appendOnly: boolean
) {
  if (!appendOnly || previousWords.length === 0) {
    return nextWords;
  }

  if (nextWords.length <= previousWords.length) {
    return previousWords;
  }

  return [...previousWords, ...nextWords.slice(previousWords.length)];
}

function splitLongCaptionPart(part: string) {
  const words = part.split(/\s+/).filter(Boolean);

  if (words.length <= 1) {
    const chunks: string[] = [];
    for (let index = 0; index < part.length; index += CAPTION_CHUNK_TARGET_CHARS) {
      chunks.push(part.slice(index, index + CAPTION_CHUNK_TARGET_CHARS));
    }
    return chunks;
  }

  const chunks: string[] = [];
  let current = "";

  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (current && next.length > CAPTION_CHUNK_TARGET_CHARS) {
      chunks.push(current);
      current = word;
    } else {
      current = next;
    }
  }

  if (current) {
    chunks.push(current);
  }

  return chunks;
}

export function splitCaptionIntoChunks(text: string) {
  const normalized = normalizeCaptionText(text);

  if (!normalized) {
    return [];
  }

  const sentenceParts = normalized.match(/[^.!?]+[.!?]?/g) ?? [normalized];
  const chunks: string[] = [];
  let current = "";

  const pushCurrent = () => {
    if (!current) return;
    chunks.push(current);
    current = "";
  };

  for (const rawPart of sentenceParts) {
    const part = rawPart.trim();
    if (!part) continue;

    if (part.length > CAPTION_CHUNK_MAX_CHARS) {
      pushCurrent();
      chunks.push(...splitLongCaptionPart(part));
      continue;
    }

    const next = current ? `${current} ${part}` : part;
    if (current && next.length > CAPTION_CHUNK_TARGET_CHARS) {
      pushCurrent();
      current = part;
    } else {
      current = next;
    }
  }

  pushCurrent();
  return chunks;
}

export function splitCaptionIntoRollingLines(
  text: string,
  maxCharsPerLine = ROLLING_CAPTION_LINE_TARGET_CHARS
) {
  return splitCaptionWordsIntoRollingLines(tokenizeCaptionWords(text), maxCharsPerLine);
}

function formatCaptionTokenLine(tokens: CaptionToken[]) {
  return tokens
    .map((token, index) => `${index > 0 && token.leadingSpace ? " " : ""}${token.text}`)
    .join("");
}

function getCaptionTokenLineLength(tokens: CaptionToken[]) {
  return formatCaptionTokenLine(tokens).length;
}

function splitCaptionWordsIntoRollingLines(words: CaptionToken[], maxCharsPerLine: number) {
  const safeMaxCharsPerLine = Math.max(12, maxCharsPerLine);
  const lines: string[] = [];
  let current: CaptionToken[] = [];

  const pushCurrent = () => {
    if (current.length === 0) return;
    lines.push(formatCaptionTokenLine(current));
    current = [];
  };

  for (const word of words) {
    if (word.text.length > safeMaxCharsPerLine) {
      pushCurrent();
      for (let index = 0; index < word.text.length; index += safeMaxCharsPerLine) {
        lines.push(word.text.slice(index, index + safeMaxCharsPerLine));
      }
      continue;
    }

    const next = [...current, word];
    if (current.length > 0 && getCaptionTokenLineLength(next) > safeMaxCharsPerLine) {
      pushCurrent();
      current = [{ ...word, leadingSpace: false }];
    } else {
      current = next;
    }
  }

  pushCurrent();
  return lines;
}

function isReadableStreamingChunk(chunk: string) {
  if (/[.!?]$/.test(chunk.trim())) {
    return true;
  }

  const wordCount = chunk.split(/\s+/).filter(Boolean).length;
  return (
    chunk.length >= MIN_STREAMING_TRAILING_CHUNK_CHARS ||
    wordCount >= MIN_STREAMING_TRAILING_CHUNK_WORDS
  );
}

export function useRollingCaptionText({
  text,
  segmentId,
  isFinal = false,
}: RollingCaptionTextOptions) {
  const captionChunks = useMemo(() => (text ? splitCaptionIntoChunks(text) : []), [text]);
  const captionVersion = `${segmentId ?? "no-segment"}:${isFinal ? "final" : "streaming"}`;

  return useMemo(() => {
    if (captionChunks.length === 0) {
      return "";
    }

    if (!isFinal && captionChunks.length > 1) {
      const trailingChunk = captionChunks[captionChunks.length - 1];
      if (!isReadableStreamingChunk(trailingChunk)) {
        return captionChunks[captionChunks.length - 2];
      }
    }

    return captionChunks[captionChunks.length - 1];
  }, [captionChunks, captionVersion]);
}

export function useRollingCaptionLines({
  text,
  segmentId,
  maxCharsPerLine = ROLLING_CAPTION_LINE_TARGET_CHARS,
  maxLines = ROLLING_CAPTION_MAX_LINES,
  appendOnly = true,
}: RollingCaptionLinesOptions) {
  const stateRef = useRef<{
    segmentKey: string | null;
    words: CaptionToken[];
  }>({
    segmentKey: null,
    words: [],
  });
  const normalizedText = useMemo(() => (text ? normalizeCaptionText(text) : ""), [text]);
  const segmentKey = segmentId ?? "no-segment";
  const nextWords = useMemo(() => tokenizeCaptionWords(normalizedText), [normalizedText]);
  const previousState = stateRef.current;
  const shouldReset =
    previousState.segmentKey !== segmentKey ||
    (!segmentId &&
      previousState.words.length > 0 &&
      nextWords.length > 0 &&
      nextWords[0].text !== previousState.words[0].text);

  if (!normalizedText || nextWords.length === 0) {
    stateRef.current = { segmentKey, words: [] };
  } else if (shouldReset) {
    stateRef.current = { segmentKey, words: nextWords };
  } else {
    stateRef.current = {
      segmentKey,
      words: appendCaptionWords(previousState.words, nextWords, appendOnly),
    };
  }

  const lineWindowSize = Math.max(1, maxLines);
  const lines = splitCaptionWordsIntoRollingLines(stateRef.current.words, maxCharsPerLine);
  return lines.slice(-lineWindowSize);
}
